import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/ui/Header';
import Sidebar from '../components/ui/Sidebar';
import Breadcrumb from '../components/ui/Breadcrumb';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import axiosInstance from '../utils/axios';
import { toast } from 'react-hot-toast';

// Built-in Platform Documentation Guides
const PLATFORM_GUIDES = [
  {
    id: 'guide-asset-registration',
    title: 'Asset Registration & Multi-Software Association',
    category: 'Asset Management',
    readTime: '4 min read',
    icon: 'Package',
    badge: 'Core Workflow',
    summary: 'Step-by-step instructions for adding assets, auto-generating unique barcodes, and linking multiple software licenses with seat allocation.',
    steps: [
      {
        step: 1,
        title: 'Navigate to Asset Inventory',
        detail: 'Go to Asset Operations → Asset Dashboard in the sidebar and click the "+ Add Asset" button in the top right.'
      },
      {
        step: 2,
        title: 'Enter Hardware Specifications & Tag',
        detail: 'Provide the asset name, serial number, model, purchase date, and cost. The system automatically creates a unique QR/Barcode asset tag.'
      },
      {
        step: 3,
        title: 'Select Multiple Software & Licences',
        detail: 'Under the "Software & Licences" dropdown, select one or more installed software applications (e.g. Windows 11 Pro, Office 365, AutoCAD). The system reserves available seats automatically.'
      },
      {
        step: 4,
        title: 'Assign Custodian & Automated Confirmation',
        detail: 'Choose the assigned user or department. Upon saving, AssetTracker Pro immediately triggers an automated email notification to the recipient with asset specifications and handover terms.'
      }
    ],
    tips: [
      'You can associate as many software licenses as needed per asset.',
      'If a software license has 0 available seats, the system alerts you before saving.'
    ]
  },
  {
    id: 'guide-lifecycle-pipeline',
    title: '5-Stage Asset Lifecycle & Custody Transfers',
    category: 'Asset Lifecycle',
    readTime: '5 min read',
    icon: 'Workflow',
    badge: 'Operations',
    summary: 'Manage physical assets through the five distinct lifecycle stages from procurement to retirement.',
    steps: [
      {
        step: 1,
        title: 'In Stock (Warehouse / IT Storage)',
        detail: 'Assets delivered by vendors are logged, QA-verified, tagged with barcodes, and placed in central storage.'
      },
      {
        step: 2,
        title: 'Assigned / In Use (Active Custody)',
        detail: 'Assets assigned to employees or departments. Custody receipts and digital acceptance are recorded.'
      },
      {
        step: 3,
        title: 'Under Maintenance & AMC Repairs',
        detail: 'Track temporary handovers to third-party vendors for hardware replacement, warranty claims, or upgrades.'
      },
      {
        step: 4,
        title: 'Quarterly Physical Audit',
        detail: 'Periodic barcode scanning and location verification to reconcile discrepancy reports.'
      },
      {
        step: 5,
        title: 'Decommissioning & Clearance',
        detail: 'Hard drive sanitization, software license revocation, exit clearance certification, and recycling or disposal.'
      }
    ],
    tips: [
      'Transitioning status to "Maintenance" automatically updates vendor warranty logs.',
      'Transfers between employees require manager approval if configured under Notification Rules.'
    ]
  },
  {
    id: 'guide-barcode-qr',
    title: 'QR Code & Barcode Scanning & Label Printing',
    category: 'Hardware & Tagging',
    readTime: '3 min read',
    icon: 'QrCode',
    badge: 'Hardware',
    summary: 'Generate standardized Code-128 barcodes and 2D QR codes for instant physical tracking and audit scanning.',
    steps: [
      {
        step: 1,
        title: 'Generate Labels for Individual or Batch Assets',
        detail: 'Select an asset in the dashboard and click "Print Label" or select multiple items for batch label sheet export.'
      },
      {
        step: 2,
        title: 'Select Label Dimensions',
        detail: 'Choose between standard industrial formats: 2" x 1" (Standard Asset Tag) or 3" x 2" (Detailed Tag with Company Logo & Serial).'
      },
      {
        step: 3,
        title: 'Scan with Mobile or Handheld Scanners',
        detail: 'Any USB, Bluetooth, or camera-based barcode scanner instantly loads the asset profile without manual typing.'
      }
    ],
    tips: [
      'Labels include durable polyester or tamper-evident vinyl recommendations for industrial setups.',
      'Scanned QR codes can be used on mobile devices to perform instant field audits.'
    ]
  },
  {
    id: 'guide-audits-compliance',
    title: 'Quarterly Audit Workflow & Discrepancy Reporting',
    category: 'Compliance',
    readTime: '4 min read',
    icon: 'ClipboardCheck',
    badge: 'Auditing',
    summary: 'Conduct fast, paperless physical audits across floors, buildings, and remote locations with zero downtime.',
    steps: [
      {
        step: 1,
        title: 'Initialize Audit Campaign',
        detail: 'Under Compliance & Maintenance → Audit Workflow, click "New Audit Session" and select target branches or departments.'
      },
      {
        step: 2,
        title: 'Live Scanning Mode',
        detail: 'Auditors scan asset tags sequentially. Verified items are marked green in real-time.'
      },
      {
        step: 3,
        title: 'Reconcile Missing & Unregistered Items',
        detail: 'Items not found after the audit window are flagged as "Discrepancy" and dispatched for investigation.'
      },
      {
        step: 4,
        title: 'Sign-off & Audit Trail Export',
        detail: 'Generate comprehensive PDF and Excel audit sign-off summaries for internal compliance and external auditors.'
      }
    ],
    tips: [
      'Auditors can log condition notes (e.g. Good, Scratched, Damaged Screen) during the scan.'
    ]
  },
  {
    id: 'guide-appearance-dark-mode',
    title: 'Appearance: Light, Dark, and System Theme Customization',
    category: 'System Configuration',
    readTime: '2 min read',
    icon: 'Moon',
    badge: 'Settings',
    summary: 'Personalize the interface theme with full Light, Dark, and dynamic System OS synchronization.',
    steps: [
      {
        step: 1,
        title: 'Quick Header Switcher',
        detail: 'Click the Sun / Moon / Monitor icon located in the top navigation header bar.'
      },
      {
        step: 2,
        title: 'Choose Theme Mode',
        detail: 'Select Light Mode for bright environments, Dark Mode for reduced eye strain, or System Mode for automatic OS following.'
      },
      {
        step: 3,
        title: 'Preferences Synchronization',
        detail: 'Your choice is saved across your browser tabs and remembered whenever you sign in.'
      }
    ],
    tips: [
      'In System mode, if your operating system switches between daylight and dusk, the app switches synchronously without refreshing.'
    ]
  },
  {
    id: 'guide-exit-clearance',
    title: 'Employee Exit Clearance & Asset Surrender',
    category: 'HR & Offboarding',
    readTime: '3 min read',
    icon: 'ShieldCheck',
    badge: 'Clearance',
    summary: 'Ensure 100% hardware recovery and software license unbinding when employees leave the organization.',
    steps: [
      {
        step: 1,
        title: 'Initiate Clearance Request',
        detail: 'Select the departing employee from User Management or Asset Dashboard → Recovery tab.'
      },
      {
        step: 2,
        title: 'Inspect & Surrender Hardware',
        detail: 'Verify laptops, monitors, access badges, and mobile devices against the assigned hardware register.'
      },
      {
        step: 3,
        title: 'Release Software Seats & Clear Credentials',
        detail: 'One-click unbinding returns software license seats back to the unallocated pool.'
      },
      {
        step: 4,
        title: 'Generate Digital Clearance Certificate',
        detail: 'Print or export the official clearance certificate signed by IT Admin and Custodian.'
      }
    ],
    tips: [
      'A digital clearance record is permanently attached to the user audit trail.'
    ]
  }
];

// Curated Video Tutorials & Walkthroughs with Real Playable Media
const VIDEO_TUTORIALS = [
  {
    id: 'vid-1',
    title: 'AssetTracker Pro: Full Platform Walkthrough & Fast Start',
    duration: '05:20',
    durationSeconds: 320,
    level: 'Beginner',
    category: 'Getting Started',
    views: '1,240',
    thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80',
    videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
    youtubeId: 'AUZIYQGPHLRV7VgWluGU6OUCcJOGNjwmjYRaCFXJk3llrAyWYCZ6mlGFvPfQKJ978VfCgu31WCnJ5negUrpo8a6nyi2e2xbMPDIuG7UiX28CShrEMFeKWxIVc-qFxhq-ujSRSy-b',
    description: 'A comprehensive tour of the asset registry, quick search, navigation sidebar, theme switching, and main KPIs.',
    keyTakeaways: [
      'Navigating between Asset Operations, Procurement, and System Config',
      'Searching assets by serial number, user name, or barcode',
      'Customizing user preferences and appearance'
    ],
    chapters: [
      { time: '00:00', seconds: 0, title: 'Overview & Main Dashboard' },
      { time: '01:10', seconds: 70, title: 'Asset Inventory Grid & Search' },
      { time: '02:30', seconds: 150, title: 'Adding New Assets & Tagging' },
      { time: '03:45', seconds: 225, title: 'System Configuration & Theme' }
    ]
  },
  {
    id: 'vid-2',
    title: 'How to Register Assets & Select Multiple Software Licences',
    duration: '04:15',
    durationSeconds: 255,
    level: 'Intermediate',
    category: 'Asset Management',
    views: '980',
    thumbnail: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80',
    videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
    youtubeId: 'AUZIYQEzIRYZ6qe0IwvHph3DfZi0-7QS26yKvs0ljEkCBfzyPzOB07ytPiUeu2VcWtZ0qDw8cJ2hnEw6AOY_wIqiAKo_IvKw7BvEB0j5ePCD7RZWNMrKYRwOnGTcdo6O2vJiYchM',
    description: 'Learn how to register workstations, select multiple software packages simultaneously, and notify the assignee.',
    keyTakeaways: [
      'Adding single or bulk hardware assets',
      'Multi-select software licenses and license seat balance tracking',
      'Automated email notification triggering upon assignment'
    ],
    chapters: [
      { time: '00:00', seconds: 0, title: 'Asset Form & Serial Numbers' },
      { time: '01:05', seconds: 65, title: 'Multi-Software License Selector' },
      { time: '02:15', seconds: 135, title: 'Seat Balance & Availability' },
      { time: '03:20', seconds: 200, title: 'Custodian Assignment & Email Alert' }
    ]
  },
  {
    id: 'vid-3',
    title: 'Quarterly Physical Audits with Barcode & QR Code Scanning',
    duration: '05:40',
    durationSeconds: 340,
    level: 'Intermediate',
    category: 'Audits & Compliance',
    views: '840',
    thumbnail: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=800&q=80',
    videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4',
    youtubeId: 'AUZIYQG9GN2-gRZ5wp3Lx7teQy-ZOWhacD39H11hhQRX46dXMpfpmoyEAaPFBBr6otcrfs_lB8y0XfhyZTywctyIXcrANjvl6ri3R1CH7r8gzarVuBz0JHD1ag6G1c8YA6dwyKNI',
    description: 'Master fast barcode scanning, resolving discrepancies, and exporting compliance audit reports.',
    keyTakeaways: [
      'Setting up audit campaigns across floors and departments',
      'Connecting barcode scanners & using mobile QR readers',
      'Flagging missing assets and closing audit sessions'
    ],
    chapters: [
      { time: '00:00', seconds: 0, title: 'Initializing an Audit Session' },
      { time: '01:20', seconds: 80, title: 'Scanning Barcodes & QR Codes' },
      { time: '03:10', seconds: 190, title: 'Reviewing Discrepancy Items' },
      { time: '04:30', seconds: 270, title: 'Exporting Sign-off Report' }
    ]
  },
  {
    id: 'vid-4',
    title: 'Employee Offboarding: Complete Asset Recovery & Clearance',
    duration: '04:50',
    durationSeconds: 290,
    level: 'Advanced',
    category: 'HR & Clearance',
    views: '720',
    thumbnail: 'https://images.unsplash.com/photo-1450133064473-71024230f91b?auto=format&fit=crop&w=800&q=80',
    videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4',
    youtubeId: 'AUZIYQE6wWUDiyUO7lltxZ0iq61of00R10xGi9iugJ82lywV-z7bD2uddhRauwoA8LE0fB03vSDZ30Fiik5qHoOCKkuH-aIHrY69c_0q3QTNw1yy4s1xdZzV3JqYQr5LjPeNH9T6',
    description: 'Step-by-step clearance workflow for returning company hardware, revoking licenses, and issuing signed certificates.',
    keyTakeaways: [
      'Surrendering assigned hardware items',
      'Freeing up software license seats for new hires',
      'Exporting signed exit clearance receipts'
    ],
    chapters: [
      { time: '00:00', seconds: 0, title: 'Offboarding Pipeline Overview' },
      { time: '01:15', seconds: 75, title: 'Hardware Inspection & Surrender' },
      { time: '02:40', seconds: 160, title: 'Software License Unbinding' },
      { time: '03:50', seconds: 230, title: 'Clearance Certificate Export' }
    ]
  },
  {
    id: 'vid-5',
    title: 'System Configuration: Session Timers, Notifications & Dark Mode',
    duration: '03:35',
    durationSeconds: 215,
    level: 'Beginner',
    category: 'System Config',
    views: '610',
    thumbnail: 'https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?auto=format&fit=crop&w=800&q=80',
    videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyBlazes.mp4',
    youtubeId: 'AUZIYQHD2aAoRVvuRMFHTy6MHqcCsyZJlQriu7byrbM2Q3emUG0KDnvGOq9zUyCtGL4BlPdBSVgpVAM6f8q-IYKO-3nUzemHurE_05mrU2p4URJTA4Wx7suxoI3c6qgvBx2_Kgum',
    description: 'Configure security session timeouts, toggle automated email rules, and manage appearance themes.',
    keyTakeaways: [
      'Setting session auto-logout time (15 mins to 24 hours)',
      'Enabling or disabling email triggers per action',
      'Selecting Light, Dark, or System theme modes'
    ],
    chapters: [
      { time: '00:00', seconds: 0, title: 'Appearance & Theme Selection' },
      { time: '01:00', seconds: 60, title: 'Session Inactivity Timeout' },
      { time: '02:00', seconds: 120, title: 'Automated Email Notification Rules' }
    ]
  }
];

const HelpSupport = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Navigation tabs: 'faq' | 'docs' | 'videos' | 'tickets' | 'contact'
  const [activeTab, setActiveTab] = useState('faq');

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // FAQs / Articles State
  const [faqs, setFaqs] = useState([]);
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total: 0,
    per_page: 20,
    last_page: 1
  });

  // Categories State
  const [categories, setCategories] = useState([
    { id: 'all', name: 'All Topics', icon: 'Grid' }
  ]);

  // Support Tickets State
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ticketStatusFilter, setTicketStatusFilter] = useState('all');
  const [ticketSearch, setTicketSearch] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [closingTicket, setClosingTicket] = useState(false);

  // New Ticket Modal State
  const [showCreateTicketModal, setShowCreateTicketModal] = useState(false);
  const [submittingTicket, setSubmittingTicket] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    category: 'general',
    priority: 'medium',
    message: '',
    attachment: null
  });

  // Docs & Video Viewer Modals
  const [activeDoc, setActiveDoc] = useState(null);
  const [showDocModal, setShowDocModal] = useState(false);
  
  // Real Video Player State
  const videoRef = useRef(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoSourceType, setVideoSourceType] = useState('html5'); // 'html5' | 'youtube'
  const [playbackRate, setPlaybackRate] = useState(1);
  const [customVideoUrl, setCustomVideoUrl] = useState('');
  const [isCustomUrlOpen, setIsCustomUrlOpen] = useState(false);
  const [tempCustomUrl, setTempCustomUrl] = useState('');

  // Stats State
  const [stats, setStats] = useState({
    total_tickets: 0,
    open_tickets: 0,
    in_progress_tickets: 0,
    resolved_tickets: 0,
    closed_tickets: 0,
    total_articles: 0,
    featured_articles: 0
  });

  // Toggle FAQ Accordion
  const toggleFAQ = (id) => {
    setExpandedFAQ(prev => (prev === id ? null : id));
  };

  // Load Categories
  const loadCategories = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/help/categories');
      if (response.data.success) {
        const apiCategories = response.data.data.map(cat => ({
          id: cat.slug,
          name: cat.name,
          icon: cat.icon || 'FileText'
        }));
        setCategories([{ id: 'all', name: 'All Topics', icon: 'Grid' }, ...apiCategories]);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  }, []);

  // Load Articles / FAQs
  const loadArticles = useCallback(async (page = 1) => {
    try {
      setLoadingArticles(true);
      const params = {
        page: page,
        per_page: pagination.per_page,
        search: searchQuery || undefined,
        category_slug: selectedCategory !== 'all' ? selectedCategory : undefined
      };

      const response = await axiosInstance.get('/help/articles', { params });
      if (response.data.success) {
        setFaqs(response.data.data.data || []);
        setPagination({
          current_page: response.data.data.current_page || 1,
          total: response.data.data.total || 0,
          per_page: response.data.data.per_page || 20,
          last_page: response.data.data.last_page || 1
        });
      }
    } catch (error) {
      console.error('Error loading articles:', error);
      toast.error('Failed to load help articles');
    } finally {
      setLoadingArticles(false);
    }
  }, [searchQuery, selectedCategory, pagination.per_page]);

  // Load Stats
  const loadStats = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/help/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  // Load Tickets
  const loadTickets = useCallback(async () => {
    try {
      setLoadingTickets(true);
      const params = {
        status: ticketStatusFilter !== 'all' ? ticketStatusFilter : undefined,
        search: ticketSearch || undefined
      };
      const response = await axiosInstance.get('/help/tickets', { params });
      if (response.data.success) {
        setTickets(response.data.data.data || []);
      }
    } catch (error) {
      console.error('Error loading tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setLoadingTickets(false);
    }
  }, [ticketStatusFilter, ticketSearch]);

  // Initial Data Fetch
  useEffect(() => {
    loadCategories();
    loadStats();
  }, [loadCategories, loadStats]);

  // Debounced FAQ Search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadArticles(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedCategory, loadArticles]);

  // Reload tickets when filter or active tab changes to tickets
  useEffect(() => {
    if (activeTab === 'tickets') {
      const timer = setTimeout(() => {
        loadTickets();
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [activeTab, ticketStatusFilter, ticketSearch, loadTickets]);

  // Handle helpful vote
  const handleHelpful = async (articleId, isHelpful) => {
    try {
      const response = await axiosInstance.post(`/help/articles/${articleId}/vote`, {
        helpful: isHelpful
      });
      if (response.data.success) {
        toast.success(isHelpful ? 'Thank you! Glad it helped.' : 'Thanks for the feedback. We will improve this.');
        loadArticles(pagination.current_page);
      }
    } catch (error) {
      console.error('Error voting:', error);
      toast.error('Failed to record vote');
    }
  };

  // View Ticket Details
  const handleViewTicket = async (ticketId) => {
    try {
      const response = await axiosInstance.get(`/help/tickets/${ticketId}`);
      if (response.data.success) {
        setSelectedTicket(response.data.data);
        setShowTicketModal(true);
      }
    } catch (error) {
      console.error('Error loading ticket:', error);
      toast.error('Failed to load ticket details');
    }
  };

  // Submit New Ticket
  const handleSubmitTicket = async (e) => {
    if (e) e.preventDefault();
    if (!ticketForm.subject.trim()) {
      toast.error('Please enter a ticket subject');
      return;
    }
    if (!ticketForm.message.trim()) {
      toast.error('Please describe your issue or inquiry');
      return;
    }

    setSubmittingTicket(true);
    try {
      const formData = new FormData();
      formData.append('subject', ticketForm.subject);
      formData.append('category', ticketForm.category);
      formData.append('priority', ticketForm.priority);
      formData.append('message', ticketForm.message);
      if (ticketForm.attachment) {
        formData.append('attachment', ticketForm.attachment);
      }

      const response = await axiosInstance.post('/help/tickets', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success('Support ticket created successfully!');
        setTicketForm({
          subject: '',
          category: 'general',
          priority: 'medium',
          message: '',
          attachment: null
        });
        setShowCreateTicketModal(false);
        loadTickets();
        loadStats();
        setActiveTab('tickets');
      }
    } catch (error) {
      console.error('Error submitting ticket:', error);
      toast.error(error.response?.data?.message || 'Failed to submit ticket');
    } finally {
      setSubmittingTicket(false);
    }
  };

  // Reply to Ticket
  const handleReplyTicket = async () => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSubmittingReply(true);
    try {
      const response = await axiosInstance.post(`/help/tickets/${selectedTicket.id}/reply`, {
        message: replyMessage
      });
      if (response.data.success) {
        toast.success('Reply submitted');
        setReplyMessage('');
        // Refresh ticket details
        handleViewTicket(selectedTicket.id);
        loadTickets();
        loadStats();
      }
    } catch (error) {
      console.error('Error replying to ticket:', error);
      toast.error(error.response?.data?.message || 'Failed to send reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  // Close Ticket
  const handleCloseTicket = async (ticketId) => {
    setClosingTicket(true);
    try {
      const response = await axiosInstance.put(`/help/tickets/${ticketId}/close`);
      if (response.data.success) {
        toast.success('Ticket marked as closed');
        if (selectedTicket && selectedTicket.id === ticketId) {
          setSelectedTicket(prev => prev ? { ...prev, status: 'closed' } : null);
        }
        loadTickets();
        loadStats();
      }
    } catch (error) {
      console.error('Error closing ticket:', error);
      toast.error(error.response?.data?.message || 'Failed to close ticket');
    } finally {
      setClosingTicket(false);
    }
  };

  // Reopen Ticket
  const handleReopenTicket = async (ticketId) => {
    try {
      const response = await axiosInstance.put(`/help/tickets/${ticketId}/reopen`);
      if (response.data.success) {
        toast.success('Ticket reopened successfully');
        if (selectedTicket && selectedTicket.id === ticketId) {
          setSelectedTicket(prev => prev ? { ...prev, status: 'open' } : null);
        }
        loadTickets();
        loadStats();
      }
    } catch (error) {
      console.error('Error reopening ticket:', error);
      toast.error(error.response?.data?.message || 'Failed to reopen ticket');
    }
  };

  // Pre-fill ticket from FAQ
  const handleAskSupportFromFAQ = (faq) => {
    setTicketForm({
      subject: `Help with: ${faq.title}`,
      category: faq.category?.slug || 'general',
      priority: 'medium',
      message: `I was reading the article "${faq.title}" but still have questions:\n\n`,
      attachment: null
    });
    setShowCreateTicketModal(true);
  };

  // Video Handlers
  const handleOpenVideo = (video) => {
    setActiveVideo(video);
    setCustomVideoUrl('');
    setPlaybackRate(1);
    setVideoSourceType('html5');
    setShowVideoModal(true);
  };

  const handleSeekToChapter = (seconds) => {
    if (videoRef.current && videoSourceType === 'html5') {
      videoRef.current.currentTime = seconds;
      videoRef.current.play().catch(() => {});
    }
  };

  const handleSetPlaybackSpeed = (speed) => {
    setPlaybackRate(speed);
    if (videoRef.current) {
      videoRef.current.playbackRate = speed;
    }
  };

  const handleSwitchActiveVideo = (vid) => {
    setActiveVideo(vid);
    setCustomVideoUrl('');
    setPlaybackRate(1);
    if (videoRef.current) {
      videoRef.current.playbackRate = 1;
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  };

  const handlePlayPreviousVideo = () => {
    if (!activeVideo) return;
    const idx = VIDEO_TUTORIALS.findIndex(v => v.id === activeVideo.id);
    if (idx > 0) {
      handleSwitchActiveVideo(VIDEO_TUTORIALS[idx - 1]);
    }
  };

  const handlePlayNextVideo = () => {
    if (!activeVideo) return;
    const idx = VIDEO_TUTORIALS.findIndex(v => v.id === activeVideo.id);
    if (idx < VIDEO_TUTORIALS.length - 1) {
      handleSwitchActiveVideo(VIDEO_TUTORIALS[idx + 1]);
    }
  };

  // Auto-play video when modal opens
  useEffect(() => {
    if (showVideoModal && videoRef.current && videoSourceType === 'html5') {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(err => {
        console.log('Video autoplay deferred until user interaction', err);
      });
    }
  }, [showVideoModal, activeVideo, videoSourceType]);

  // Badge Color Helpers
  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'critical':
        return 'bg-error/15 text-error border-error/30';
      case 'high':
        return 'bg-orange-500/15 text-orange-500 border-orange-500/30';
      case 'medium':
        return 'bg-warning/15 text-warning border-warning/30';
      case 'low':
      default:
        return 'bg-success/15 text-success border-success/30';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return 'bg-warning/15 text-warning border-warning/30';
      case 'in_progress':
        return 'bg-primary/15 text-primary border-primary/30';
      case 'resolved':
        return 'bg-success/15 text-success border-success/30';
      case 'closed':
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  // Filtered Guides
  const filteredGuides = useMemo(() => {
    if (!searchQuery) return PLATFORM_GUIDES;
    const query = searchQuery.toLowerCase();
    return PLATFORM_GUIDES.filter(
      guide =>
        guide.title.toLowerCase().includes(query) ||
        guide.category.toLowerCase().includes(query) ||
        guide.summary.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Filtered Video Tutorials
  const filteredVideos = useMemo(() => {
    if (!searchQuery) return VIDEO_TUTORIALS;
    const query = searchQuery.toLowerCase();
    return VIDEO_TUTORIALS.filter(
      vid =>
        vid.title.toLowerCase().includes(query) ||
        vid.category.toLowerCase().includes(query) ||
        vid.description.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors duration-200">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />

      <main className={`pt-16 transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-64 lg:ml-72'}`}>
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-8">
          
          {/* Breadcrumb */}
          <div className="flex items-center justify-between">
            <Breadcrumb />
            <div className="flex items-center space-x-2">
              <span className="flex h-2.5 w-2.5 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-success"></span>
              </span>
              <span className="text-xs text-muted-foreground font-medium">All Systems Operational</span>
            </div>
          </div>

          {/* Hero Banner */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-card to-card border border-border p-6 sm:p-8 lg:p-10 shadow-soft">
            <div className="relative z-10 max-w-3xl">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-4 border border-primary/20">
                <Icon name="Sparkles" size={14} />
                <span>Help & Knowledge Center</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">
                How can we assist you today?
              </h1>
              <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed">
                Search verified answers, browse step-by-step platform guides, watch real video walkthroughs, or connect directly with our IT support engineers.
              </p>

              {/* Global Search Bar */}
              <div className="mt-6 relative max-w-2xl">
                <div className="relative flex items-center">
                  <Icon 
                    name="Search" 
                    size={20} 
                    className="absolute left-4 text-muted-foreground pointer-events-none" 
                  />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search FAQs, guides, video tutorials (e.g. 'multi-software', 'barcodes', 'audit', 'theme')..."
                    className="w-full pl-12 pr-10 py-3.5 bg-background text-foreground rounded-xl border border-border shadow-sm placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
                      title="Clear search"
                    >
                      <Icon name="X" size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Decorative background glow */}
            <div className="absolute -right-20 -bottom-20 w-80 h-80 bg-primary/10 rounded-full blur-3xl pointer-events-none"></div>
          </div>

          {/* Quick Metrics & System Highlights */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            <div className="bg-card border border-border rounded-xl p-4 shadow-subtle hover:border-primary/40 transition-colors">
              <div className="flex items-center justify-between text-muted-foreground mb-1">
                <span className="text-xs font-medium uppercase tracking-wider">Total FAQs</span>
                <Icon name="FileText" size={16} className="text-primary" />
              </div>
              <p className="text-2xl font-bold text-foreground">{stats.total_articles || faqs.length || 12}</p>
              <span className="text-xs text-muted-foreground">Verified guides</span>
            </div>

            <div className="bg-card border border-border rounded-xl p-4 shadow-subtle hover:border-warning/40 transition-colors">
              <div className="flex items-center justify-between text-muted-foreground mb-1">
                <span className="text-xs font-medium uppercase tracking-wider">Open Tickets</span>
                <Icon name="Clock" size={16} className="text-warning" />
              </div>
              <p className="text-2xl font-bold text-warning">{stats.open_tickets || 0}</p>
              <span className="text-xs text-muted-foreground">Pending response</span>
            </div>

            <div className="bg-card border border-border rounded-xl p-4 shadow-subtle hover:border-primary/40 transition-colors">
              <div className="flex items-center justify-between text-muted-foreground mb-1">
                <span className="text-xs font-medium uppercase tracking-wider">In Progress</span>
                <Icon name="RefreshCw" size={16} className="text-primary" />
              </div>
              <p className="text-2xl font-bold text-primary">{stats.in_progress_tickets || 0}</p>
              <span className="text-xs text-muted-foreground">Being addressed</span>
            </div>

            <div className="bg-card border border-border rounded-xl p-4 shadow-subtle hover:border-success/40 transition-colors">
              <div className="flex items-center justify-between text-muted-foreground mb-1">
                <span className="text-xs font-medium uppercase tracking-wider">Resolved</span>
                <Icon name="CheckCircle" size={16} className="text-success" />
              </div>
              <p className="text-2xl font-bold text-success">{stats.resolved_tickets || 0}</p>
              <span className="text-xs text-muted-foreground">Completed tickets</span>
            </div>

            <div className="bg-card border border-border rounded-xl p-4 shadow-subtle hover:border-primary/40 transition-colors">
              <div className="flex items-center justify-between text-muted-foreground mb-1">
                <span className="text-xs font-medium uppercase tracking-wider">Avg Response</span>
                <Icon name="Zap" size={16} className="text-accent" />
              </div>
              <p className="text-2xl font-bold text-foreground">&lt; 2 hrs</p>
              <span className="text-xs text-muted-foreground">SLA guaranteed</span>
            </div>

            <div className="bg-card border border-border rounded-xl p-4 shadow-subtle hover:border-primary/40 transition-colors">
              <div className="flex items-center justify-between text-muted-foreground mb-1">
                <span className="text-xs font-medium uppercase tracking-wider">Support Hours</span>
                <Icon name="Headphones" size={16} className="text-success" />
              </div>
              <p className="text-2xl font-bold text-foreground">24 / 7</p>
              <span className="text-xs text-muted-foreground">Portal & Hotline</span>
            </div>
          </div>

          {/* Navigation Sub-Tabs */}
          <div className="flex items-center space-x-2 border-b border-border pb-1 overflow-x-auto scrollbar-none">
            <button
              onClick={() => setActiveTab('faq')}
              className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === 'faq'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name="HelpCircle" size={16} />
              <span>FAQs & Answers</span>
            </button>

            <button
              onClick={() => setActiveTab('docs')}
              className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === 'docs'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name="BookOpen" size={16} />
              <span>Platform Guides ({PLATFORM_GUIDES.length})</span>
            </button>

            <button
              onClick={() => setActiveTab('videos')}
              className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === 'videos'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name="Video" size={16} />
              <span>Video Tutorials ({VIDEO_TUTORIALS.length})</span>
            </button>

            <button
              onClick={() => setActiveTab('tickets')}
              className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === 'tickets'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name="Ticket" size={16} />
              <span>Support Tickets</span>
              {stats.open_tickets > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                  activeTab === 'tickets' ? 'bg-primary-foreground text-primary' : 'bg-warning text-warning-foreground'
                }`}>
                  {stats.open_tickets}
                </span>
              )}
            </button>

            <button
              onClick={() => setActiveTab('contact')}
              className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === 'contact'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name="PhoneCall" size={16} />
              <span>Contact IT Desk</span>
            </button>
          </div>

          {/* TAB 1: KNOWLEDGE BASE & FAQS */}
          {activeTab === 'faq' && (
            <div className="space-y-6">
              
              {/* Category Pills */}
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`flex items-center space-x-2 px-3.5 py-2 rounded-lg border text-xs sm:text-sm font-medium transition-all ${
                      selectedCategory === cat.id
                        ? 'border-primary bg-primary/10 text-primary shadow-xs'
                        : 'border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground'
                    }`}
                  >
                    <Icon name={cat.icon || 'Folder'} size={15} />
                    <span>{cat.name}</span>
                  </button>
                ))}
              </div>

              {/* FAQ Accordion List Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-foreground">Frequently Asked Questions</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Click any question to expand detailed instructions and solutions.
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCreateTicketModal(true)}
                  >
                    <Icon name="Plus" size={14} className="mr-1.5" />
                    Submit Request
                  </Button>
                </div>
              </div>

              {/* FAQ Accordion Cards */}
              {loadingArticles ? (
                <div className="flex flex-col items-center justify-center py-16 space-y-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <span className="text-sm text-muted-foreground">Loading knowledge articles...</span>
                </div>
              ) : faqs.length > 0 ? (
                <div className="space-y-3">
                  {faqs.map((faq) => {
                    const isExpanded = expandedFAQ === faq.id;
                    return (
                      <div
                        key={faq.id}
                        className={`bg-card border rounded-xl overflow-hidden transition-all duration-200 ${
                          isExpanded 
                            ? 'border-primary shadow-subtle ring-1 ring-primary/20' 
                            : 'border-border hover:border-border/80 hover:shadow-xs'
                        }`}
                      >
                        {/* Accordion Trigger Header */}
                        <button
                          type="button"
                          onClick={() => toggleFAQ(faq.id)}
                          className="w-full text-left p-4 sm:p-5 flex items-center justify-between space-x-4 cursor-pointer focus:outline-none"
                        >
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className={`p-2 rounded-lg flex-shrink-0 ${
                              isExpanded ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                            }`}>
                              <Icon name="HelpCircle" size={18} />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center space-x-2 mb-1 flex-wrap gap-1">
                                {faq.category?.name && (
                                  <span className="text-[11px] font-semibold uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-md">
                                    {faq.category.name}
                                  </span>
                                )}
                                {faq.is_featured && (
                                  <span className="text-[11px] font-semibold text-accent bg-accent/15 px-2 py-0.5 rounded-md">
                                    Featured
                                  </span>
                                )}
                              </div>
                              <h3 className="text-base font-semibold text-foreground truncate-2-lines">
                                {faq.title}
                              </h3>
                            </div>
                          </div>

                          <div className="flex items-center space-x-3 flex-shrink-0">
                            <span className="text-xs text-muted-foreground hidden sm:inline">
                              {faq.views || 0} views
                            </span>
                            <div className={`p-1 rounded-full text-muted-foreground transition-transform duration-200 ${
                              isExpanded ? 'rotate-180 text-primary' : ''
                            }`}>
                              <Icon name="ChevronDown" size={20} />
                            </div>
                          </div>
                        </button>

                        {/* Accordion Expandable Content */}
                        {isExpanded && (
                          <div className="px-5 pb-5 pt-1 border-t border-border/50 animate-in fade-in-50 duration-200 space-y-4">
                            <div className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap pl-11">
                              {faq.content}
                            </div>

                            {/* Helpfulness Feedback & Actions */}
                            <div className="pt-4 border-t border-border/50 flex flex-wrap items-center justify-between gap-3 pl-11">
                              <div className="flex items-center space-x-3 text-xs">
                                <span className="text-muted-foreground">Was this answer helpful?</span>
                                <button
                                  type="button"
                                  onClick={() => handleHelpful(faq.id, true)}
                                  className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-md border border-border bg-card hover:bg-success/10 hover:border-success/30 hover:text-success text-muted-foreground transition-colors"
                                >
                                  <Icon name="ThumbsUp" size={13} />
                                  <span>Yes ({faq.helpful_count || 0})</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleHelpful(faq.id, false)}
                                  className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-md border border-border bg-card hover:bg-error/10 hover:border-error/30 hover:text-error text-muted-foreground transition-colors"
                                >
                                  <Icon name="ThumbsDown" size={13} />
                                  <span>No</span>
                                </button>
                              </div>

                              <div className="flex items-center space-x-2">
                                <Button
                                  variant="ghost"
                                  size="xs"
                                  onClick={() => {
                                    setSelectedArticle(faq);
                                    setShowArticleModal(true);
                                  }}
                                >
                                  <Icon name="Maximize2" size={13} className="mr-1" />
                                  Full View
                                </Button>
                                <Button
                                  variant="outline"
                                  size="xs"
                                  onClick={() => handleAskSupportFromFAQ(faq)}
                                >
                                  <Icon name="MessageSquare" size={13} className="mr-1" />
                                  Ask Support About This
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-card border border-border rounded-2xl p-12 text-center space-y-4">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto text-muted-foreground">
                    <Icon name="Search" size={32} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">No matching articles found</h3>
                    <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                      We couldn't find any articles matching "{searchQuery}". Try broadening your search or submit a direct support ticket.
                    </p>
                  </div>
                  <div className="pt-2 flex justify-center space-x-3">
                    <Button variant="outline" size="sm" onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}>
                      Reset Filters
                    </Button>
                    <Button variant="default" size="sm" onClick={() => setShowCreateTicketModal(true)}>
                      Submit Support Ticket
                    </Button>
                  </div>
                </div>
              )}

              {/* Pagination */}
              {pagination.total > pagination.per_page && (
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                    {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} articles
                  </p>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.current_page === 1}
                      onClick={() => loadArticles(pagination.current_page - 1)}
                    >
                      Previous
                    </Button>
                    <span className="text-xs font-medium px-2">
                      {pagination.current_page} / {pagination.last_page}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.current_page >= pagination.last_page}
                      onClick={() => loadArticles(pagination.current_page + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: PLATFORM DOCUMENTATION & USER GUIDES */}
          {activeTab === 'docs' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-foreground">Platform Documentation & Workflows</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Official operational guidelines for AssetTracker Pro modules and enterprise workflows.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.print()}
                  className="hidden sm:inline-flex"
                >
                  <Icon name="Printer" size={14} className="mr-1.5" />
                  Print Documentation
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredGuides.map((guide) => (
                  <div
                    key={guide.id}
                    className="bg-card border border-border rounded-xl p-5 flex flex-col justify-between hover:shadow-moderate hover:border-primary/40 transition-all group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover:scale-105 transition-transform">
                          <Icon name={guide.icon} size={20} />
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-[11px] font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full border border-primary/20">
                            {guide.badge}
                          </span>
                          <span className="text-xs text-muted-foreground flex items-center">
                            <Icon name="Clock" size={12} className="mr-1" />
                            {guide.readTime}
                          </span>
                        </div>
                      </div>

                      <h3 className="font-semibold text-base text-foreground group-hover:text-primary transition-colors">
                        {guide.title}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-3 leading-relaxed">
                        {guide.summary}
                      </p>

                      <div className="mt-4 pt-3 border-t border-border">
                        <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider block mb-2">
                          Key Steps ({guide.steps.length})
                        </span>
                        <ul className="space-y-1.5">
                          {guide.steps.slice(0, 3).map((st) => (
                            <li key={st.step} className="text-xs text-foreground/80 flex items-start space-x-2">
                              <span className="w-4 h-4 rounded-full bg-muted text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                                {st.step}
                              </span>
                              <span className="truncate">{st.title}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="mt-5 pt-3 border-t border-border flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{guide.category}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setActiveDoc(guide);
                          setShowDocModal(true);
                        }}
                      >
                        Read Full Guide
                        <Icon name="ArrowRight" size={14} className="ml-1" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 3: VIDEO TUTORIALS & WALKTHROUGHS */}
          {activeTab === 'videos' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-foreground">Interactive Video Tutorials</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Click any tutorial to launch the real-time video player with interactive chapters and playback speed controls.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredVideos.map((video) => (
                  <div
                    key={video.id}
                    className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-moderate hover:border-primary/40 transition-all flex flex-col justify-between group"
                  >
                    <div>
                      {/* Video Thumbnail with Hover Overlay & Play Action */}
                      <div 
                        onClick={() => handleOpenVideo(video)}
                        className="relative h-48 bg-gray-900 flex items-center justify-center cursor-pointer overflow-hidden group"
                      >
                        {video.thumbnail && (
                          <img
                            src={video.thumbnail}
                            alt={video.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 opacity-80 group-hover:opacity-95"
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                        
                        {/* Glowing Play Button */}
                        <div className="absolute w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-2xl group-hover:scale-110 transition-all ring-4 ring-primary/30">
                          <Icon name="Play" size={24} className="ml-1 fill-current" />
                        </div>

                        <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded-md bg-black/80 backdrop-blur-xs text-white text-xs font-semibold flex items-center space-x-1">
                          <Icon name="Clock" size={12} />
                          <span>{video.duration}</span>
                        </div>
                        <div className="absolute top-3 left-3 px-2 py-0.5 rounded-md bg-primary text-primary-foreground text-[11px] font-semibold">
                          {video.level}
                        </div>
                        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-md bg-black/70 text-white text-[10px] font-medium flex items-center space-x-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-success"></span>
                          <span>HD Video</span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-5">
                        <div className="flex items-center space-x-2 text-xs text-muted-foreground mb-1.5">
                          <span>{video.category}</span>
                          <span>•</span>
                          <span>{video.views} views</span>
                        </div>
                        <h3 
                          onClick={() => handleOpenVideo(video)}
                          className="font-semibold text-base text-foreground group-hover:text-primary transition-colors cursor-pointer"
                        >
                          {video.title}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-2 line-clamp-2 leading-relaxed">
                          {video.description}
                        </p>
                      </div>
                    </div>

                    <div className="px-5 pb-5 pt-2 border-t border-border flex items-center justify-between">
                      <span className="text-xs font-medium text-primary">Interactive Player</span>
                      <Button
                        variant="default"
                        size="xs"
                        onClick={() => handleOpenVideo(video)}
                      >
                        <Icon name="Play" size={13} className="mr-1 fill-current" />
                        Play Video
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 4: SUPPORT TICKETS & CONVERSATIONS */}
          {activeTab === 'tickets' && (
            <div className="space-y-6">
              
              {/* Header and Action */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-foreground">My Support Tickets</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Track existing assistance inquiries, reply to IT technicians, or raise a new support ticket.
                  </p>
                </div>
                <Button
                  variant="default"
                  onClick={() => setShowCreateTicketModal(true)}
                  className="shadow-sm"
                >
                  <Icon name="Plus" size={15} className="mr-1.5" />
                  Create New Ticket
                </Button>
              </div>

              {/* Status Filter Tabs & Search */}
              <div className="bg-card border border-border rounded-xl p-3 flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-xs">
                {/* Filter Tabs */}
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { id: 'all', label: 'All' },
                    { id: 'open', label: 'Open' },
                    { id: 'in_progress', label: 'In Progress' },
                    { id: 'resolved', label: 'Resolved' },
                    { id: 'closed', label: 'Closed' }
                  ].map((filter) => (
                    <button
                      key={filter.id}
                      onClick={() => setTicketStatusFilter(filter.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        ticketStatusFilter === filter.id
                          ? 'bg-primary text-primary-foreground font-semibold shadow-xs'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>

                {/* Ticket Search */}
                <div className="relative w-full md:w-64">
                  <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={ticketSearch}
                    onChange={(e) => setTicketSearch(e.target.value)}
                    placeholder="Search by ticket # or subject..."
                    className="w-full pl-9 pr-3 py-1.5 bg-background text-foreground text-xs rounded-lg border border-border focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  {ticketSearch && (
                    <button
                      onClick={() => setTicketSearch('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <Icon name="X" size={13} />
                    </button>
                  )}
                </div>
              </div>

              {/* Ticket Cards */}
              {loadingTickets ? (
                <div className="flex flex-col items-center justify-center py-16 space-y-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <span className="text-sm text-muted-foreground">Loading support tickets...</span>
                </div>
              ) : tickets.length > 0 ? (
                <div className="space-y-3">
                  {tickets.map((ticket) => (
                    <div
                      key={ticket.id}
                      className="bg-card border border-border rounded-xl p-4 sm:p-5 hover:border-primary/40 hover:shadow-moderate transition-all"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        
                        {/* Info */}
                        <div className="space-y-2 flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-mono text-xs font-semibold text-primary bg-primary/10 px-2.5 py-0.5 rounded-md border border-primary/20">
                              {ticket.ticket_number}
                            </span>
                            <span className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${getStatusBadge(ticket.status)}`}>
                              {ticket.status.replace('_', ' ')}
                            </span>
                            <span className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${getPriorityBadge(ticket.priority)}`}>
                              {ticket.priority} Priority
                            </span>
                            <span className="text-xs text-muted-foreground">
                              Category: <strong className="text-foreground capitalize">{ticket.category || 'General'}</strong>
                            </span>
                          </div>

                          <h3 className="text-base font-bold text-foreground">
                            {ticket.subject}
                          </h3>

                          <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                            {ticket.message}
                          </p>

                          <div className="flex items-center space-x-4 text-xs text-muted-foreground pt-1">
                            <span className="flex items-center">
                              <Icon name="Calendar" size={13} className="mr-1" />
                              Created {new Date(ticket.created_at).toLocaleDateString()}
                            </span>
                            {ticket.replies && ticket.replies.length > 0 && (
                              <span className="flex items-center text-primary font-medium">
                                <Icon name="MessageSquare" size={13} className="mr-1" />
                                {ticket.replies.length} {ticket.replies.length === 1 ? 'reply' : 'replies'}
                              </span>
                            )}
                            {ticket.attachment && (
                              <span className="flex items-center text-accent">
                                <Icon name="Paperclip" size={13} className="mr-1" />
                                Attachment included
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex sm:flex-col items-center sm:items-end justify-between gap-2 border-t sm:border-t-0 pt-3 sm:pt-0 border-border">
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleViewTicket(ticket.id)}
                            className="w-full sm:w-auto"
                          >
                            <Icon name="MessageCircle" size={14} className="mr-1.5" />
                            View & Reply
                          </Button>

                          {ticket.status !== 'closed' && ticket.status !== 'resolved' ? (
                            <Button
                              variant="ghost"
                              size="xs"
                              onClick={() => handleCloseTicket(ticket.id)}
                              className="text-muted-foreground hover:text-error"
                            >
                              <Icon name="Check" size={13} className="mr-1" />
                              Mark Resolved
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="xs"
                              onClick={() => handleReopenTicket(ticket.id)}
                              className="text-muted-foreground hover:text-primary"
                            >
                              <Icon name="RotateCcw" size={13} className="mr-1" />
                              Reopen
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-card border border-border rounded-2xl p-12 text-center space-y-4">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto text-muted-foreground">
                    <Icon name="Ticket" size={32} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">No tickets in this status</h3>
                    <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
                      {ticketStatusFilter === 'all'
                        ? "You haven't submitted any support requests yet. We are here to help whenever you need us."
                        : `You have no tickets currently marked as "${ticketStatusFilter}".`}
                    </p>
                  </div>
                  <Button variant="default" size="sm" onClick={() => setShowCreateTicketModal(true)}>
                    Create Your First Ticket
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* TAB 5: CONTACT IT DESK & HELPLINES */}
          {activeTab === 'contact' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-xl font-bold text-foreground">Direct IT Helpdesk & Support Channels</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Reach our dedicated IT operations team through your preferred channel.
                </p>
              </div>

              {/* Contact Method Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="bg-card border border-border rounded-2xl p-6 flex flex-col justify-between hover:border-primary/40 transition-all shadow-subtle">
                  <div>
                    <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                      <Icon name="PhoneCall" size={24} />
                    </div>
                    <h3 className="text-base font-bold text-foreground">Emergency IT Hotline</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Direct phone line for high-priority outages, hardware emergencies, and urgent access issues.
                    </p>
                    <div className="mt-4 p-3 bg-muted/40 rounded-xl">
                      <span className="text-xs text-muted-foreground block">Toll-Free Phone:</span>
                      <strong className="text-sm text-foreground">+1 (800) 456-7890</strong>
                      <span className="text-[11px] text-success block mt-1">Available 24/7/365</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-5 w-full"
                    onClick={() => {
                      navigator.clipboard.writeText('+18004567890');
                      toast.success('Hotline copied to clipboard!');
                    }}
                  >
                    <Icon name="Copy" size={14} className="mr-1.5" />
                    Copy Number
                  </Button>
                </div>

                <div className="bg-card border border-border rounded-2xl p-6 flex flex-col justify-between hover:border-primary/40 transition-all shadow-subtle">
                  <div>
                    <div className="w-12 h-12 rounded-xl bg-accent/15 text-accent flex items-center justify-center mb-4">
                      <Icon name="Mail" size={24} />
                    </div>
                    <h3 className="text-base font-bold text-foreground">Email Support Desk</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Send logs, screenshots, and detailed specifications directly to our ticket dispatch engine.
                    </p>
                    <div className="mt-4 p-3 bg-muted/40 rounded-xl">
                      <span className="text-xs text-muted-foreground block">Help Desk Email:</span>
                      <strong className="text-sm text-foreground">it-support@kerol.com</strong>
                      <span className="text-[11px] text-muted-foreground block mt-1">Average response &lt; 2 hrs</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-5 w-full"
                    onClick={() => {
                      navigator.clipboard.writeText('it-support@kerol.com');
                      toast.success('Email copied to clipboard!');
                    }}
                  >
                    <Icon name="Copy" size={14} className="mr-1.5" />
                    Copy Email
                  </Button>
                </div>

                <div className="bg-card border border-border rounded-2xl p-6 flex flex-col justify-between hover:border-primary/40 transition-all shadow-subtle">
                  <div>
                    <div className="w-12 h-12 rounded-xl bg-success/15 text-success flex items-center justify-center mb-4">
                      <Icon name="Ticket" size={24} />
                    </div>
                    <h3 className="text-base font-bold text-foreground">Digital Ticket Portal</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Create an official ticket with file attachments, automatic tracking, and SLA escalation.
                    </p>
                    <div className="mt-4 p-3 bg-muted/40 rounded-xl">
                      <span className="text-xs text-muted-foreground block">Active Requests:</span>
                      <strong className="text-sm text-foreground">{stats.open_tickets} Active Inquiries</strong>
                      <span className="text-[11px] text-primary block mt-1">Trackable conversation history</span>
                    </div>
                  </div>
                  <Button
                    variant="default"
                    size="sm"
                    className="mt-5 w-full"
                    onClick={() => setShowCreateTicketModal(true)}
                  >
                    <Icon name="Plus" size={14} className="mr-1.5" />
                    Open Support Ticket
                  </Button>
                </div>
              </div>

              {/* Service Level Agreement (SLA) Matrix */}
              <div className="bg-card border border-border rounded-2xl p-6 shadow-subtle space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    <Icon name="Shield" size={18} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-foreground">Service Level Agreement (SLA) Matrix</h3>
                    <p className="text-xs text-muted-foreground">Standard target initial response and resolution windows.</p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-border text-muted-foreground">
                        <th className="py-2.5 font-semibold">Priority Level</th>
                        <th className="py-2.5 font-semibold">Criteria</th>
                        <th className="py-2.5 font-semibold">Target Response</th>
                        <th className="py-2.5 font-semibold">Target Resolution</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y border-border">
                      <tr>
                        <td className="py-3 font-medium">
                          <span className="px-2 py-0.5 rounded-full bg-error/15 text-error font-semibold">
                            Critical
                          </span>
                        </td>
                        <td className="py-3 text-muted-foreground">System outage, critical data loss, all users impacted</td>
                        <td className="py-3 font-semibold text-foreground">&lt; 30 Minutes</td>
                        <td className="py-3 text-muted-foreground">4 Hours</td>
                      </tr>
                      <tr>
                        <td className="py-3 font-medium">
                          <span className="px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-500 font-semibold">
                            High
                          </span>
                        </td>
                        <td className="py-3 text-muted-foreground">Major feature blocked, hardware malfunction for individual</td>
                        <td className="py-3 font-semibold text-foreground">&lt; 2 Hours</td>
                        <td className="py-3 text-muted-foreground">12 Hours</td>
                      </tr>
                      <tr>
                        <td className="py-3 font-medium">
                          <span className="px-2 py-0.5 rounded-full bg-warning/15 text-warning font-semibold">
                            Medium
                          </span>
                        </td>
                        <td className="py-3 text-muted-foreground">General software request, configuration question, minor bug</td>
                        <td className="py-3 font-semibold text-foreground">&lt; 4 Hours</td>
                        <td className="py-3 text-muted-foreground">24 Hours</td>
                      </tr>
                      <tr>
                        <td className="py-3 font-medium">
                          <span className="px-2 py-0.5 rounded-full bg-success/15 text-success font-semibold">
                            Low
                          </span>
                        </td>
                        <td className="py-3 text-muted-foreground">Informational inquiry, documentation clarification</td>
                        <td className="py-3 font-semibold text-foreground">&lt; 12 Hours</td>
                        <td className="py-3 text-muted-foreground">48 Hours</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

        </div>
      </main>

      {/* =========================================================================
          MODAL: CREATE NEW SUPPORT TICKET
         ========================================================================= */}
      {showCreateTicketModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in-50">
          <div className="bg-card text-card-foreground rounded-2xl border border-border shadow-2xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-border bg-card">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <Icon name="Ticket" size={20} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-foreground">Raise Support Ticket</h3>
                  <p className="text-xs text-muted-foreground">Submit a query to our IT Helpdesk</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateTicketModal(false)}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmitTicket} className="p-5 overflow-y-auto space-y-4 flex-1">
              {/* Subject */}
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">
                  Subject / Summary <span className="text-error">*</span>
                </label>
                <Input
                  value={ticketForm.subject}
                  onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
                  placeholder="e.g. Cannot bind software license to new laptop"
                  required
                />
              </div>

              {/* Category & Priority Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">
                    Category
                  </label>
                  <select
                    value={ticketForm.category}
                    onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })}
                    className="w-full px-3 py-2 text-xs rounded-lg bg-background text-foreground border border-border focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  >
                    <option value="general">General Inquiry</option>
                    <option value="hardware">Hardware & Asset Tagging</option>
                    <option value="software">Software & Licences</option>
                    <option value="audits">Audit & Compliance</option>
                    <option value="permissions">User Access & Roles</option>
                    <option value="bug">System Bug Report</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5">
                    Priority Level
                  </label>
                  <select
                    value={ticketForm.priority}
                    onChange={(e) => setTicketForm({ ...ticketForm, priority: e.target.value })}
                    className="w-full px-3 py-2 text-xs rounded-lg bg-background text-foreground border border-border focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  >
                    <option value="low">Low - General inquiry</option>
                    <option value="medium">Medium - Standard issue</option>
                    <option value="high">High - Urgent work blocker</option>
                    <option value="critical">Critical - System down</option>
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">
                  Detailed Description <span className="text-error">*</span>
                </label>
                <textarea
                  value={ticketForm.message}
                  onChange={(e) => setTicketForm({ ...ticketForm, message: e.target.value })}
                  rows={5}
                  placeholder="Please describe the issue, relevant serial numbers, error messages, or steps to reproduce..."
                  className="w-full px-3 py-2 text-xs rounded-lg bg-background text-foreground border border-border focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none placeholder:text-muted-foreground"
                  required
                />
              </div>

              {/* Attachment */}
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">
                  Attachment (Screenshot or Logs, max 5MB)
                </label>
                <div className="border border-dashed border-border rounded-xl p-3 text-center bg-muted/20 hover:bg-muted/30 transition-colors">
                  <input
                    type="file"
                    id="ticket-file-input"
                    className="hidden"
                    onChange={(e) => setTicketForm({ ...ticketForm, attachment: e.target.files[0] })}
                  />
                  {ticketForm.attachment ? (
                    <div className="flex items-center justify-between p-2 rounded-lg bg-card border border-border text-xs">
                      <div className="flex items-center space-x-2 truncate">
                        <Icon name="FileText" size={16} className="text-primary flex-shrink-0" />
                        <span className="truncate font-medium">{ticketForm.attachment.name}</span>
                        <span className="text-muted-foreground">({(ticketForm.attachment.size / 1024).toFixed(1)} KB)</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setTicketForm({ ...ticketForm, attachment: null })}
                        className="text-muted-foreground hover:text-error p-1"
                        title="Remove file"
                      >
                        <Icon name="Trash2" size={14} />
                      </button>
                    </div>
                  ) : (
                    <label htmlFor="ticket-file-input" className="cursor-pointer block py-2">
                      <Icon name="UploadCloud" size={24} className="mx-auto text-muted-foreground mb-1" />
                      <span className="text-xs font-medium text-primary hover:underline">Click to upload file</span>
                      <span className="text-[11px] text-muted-foreground block mt-0.5">PNG, JPG, PDF, or DOCX</span>
                    </label>
                  )}
                </div>
              </div>
            </form>

            {/* Modal Footer */}
            <div className="p-4 border-t border-border bg-card flex items-center justify-end space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCreateTicketModal(false)}
                disabled={submittingTicket}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleSubmitTicket}
                loading={submittingTicket}
              >
                Submit Ticket
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          MODAL: TICKET DETAILS & CONVERSATION THREAD
         ========================================================================= */}
      {showTicketModal && selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in-50">
          <div className="bg-card text-card-foreground rounded-2xl border border-border shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
            
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-border bg-card">
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <span className="font-mono text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded">
                    {selectedTicket.ticket_number}
                  </span>
                  <span className={`text-[11px] font-semibold uppercase px-2 py-0.5 rounded-full border ${getStatusBadge(selectedTicket.status)}`}>
                    {selectedTicket.status.replace('_', ' ')}
                  </span>
                  <span className={`text-[11px] font-semibold uppercase px-2 py-0.5 rounded-full border ${getPriorityBadge(selectedTicket.priority)}`}>
                    {selectedTicket.priority}
                  </span>
                </div>
                <h3 className="text-base font-bold text-foreground">
                  {selectedTicket.subject}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowTicketModal(false)}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 overflow-y-auto space-y-5 flex-1">
              
              {/* Original Query Message */}
              <div className="bg-muted/30 border border-border rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded-full bg-primary/20 text-primary text-xs font-bold flex items-center justify-center">
                      {(selectedTicket.user?.name || 'U').charAt(0).toUpperCase()}
                    </div>
                    <span className="font-semibold text-foreground">{selectedTicket.user?.name || 'You'}</span>
                    <span>(Original Request)</span>
                  </div>
                  <span>{new Date(selectedTicket.created_at).toLocaleString()}</span>
                </div>

                <div className="text-xs text-foreground/90 whitespace-pre-wrap leading-relaxed pl-8">
                  {selectedTicket.message}
                </div>

                {/* Attachment Link */}
                {selectedTicket.attachment && (
                  <div className="pl-8 pt-2">
                    <a
                      href={selectedTicket.attachment_url || `/storage/${selectedTicket.attachment}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-card border border-border text-xs text-primary hover:underline"
                    >
                      <Icon name="Download" size={14} />
                      <span>Download Attachment</span>
                    </a>
                  </div>
                )}
              </div>

              {/* Conversation Replies Thread */}
              <div className="space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Conversation Thread ({selectedTicket.replies?.length || 0})
                </h4>

                {selectedTicket.replies && selectedTicket.replies.length > 0 ? (
                  selectedTicket.replies.map((reply) => {
                    const isStaff = reply.is_internal || reply.user?.role?.name?.toLowerCase().includes('admin');
                    return (
                      <div
                        key={reply.id}
                        className={`p-3.5 rounded-xl border text-xs space-y-2 ${
                          isStaff
                            ? 'bg-primary/5 border-primary/20 ml-4'
                            : 'bg-card border-border mr-4'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                              isStaff ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                            }`}>
                              {(reply.user?.name || 'S').charAt(0).toUpperCase()}
                            </div>
                            <span className="font-semibold text-foreground">
                              {reply.user?.name || 'Support Desk'}
                            </span>
                            {isStaff && (
                              <span className="text-[10px] bg-primary/20 text-primary px-1.5 py-0.2 rounded font-semibold">
                                IT Support
                              </span>
                            )}
                          </div>
                          <span className="text-[11px] text-muted-foreground">
                            {new Date(reply.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })},{' '}
                            {new Date(reply.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-foreground/90 whitespace-pre-wrap leading-relaxed pl-8">
                          {reply.message}
                        </p>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-xs text-muted-foreground italic pl-2">
                    No replies yet. An IT support engineer will follow up shortly.
                  </p>
                )}
              </div>

              {/* Reply Box */}
              {selectedTicket.status !== 'closed' ? (
                <div className="pt-3 border-t border-border space-y-2.5">
                  <label className="block text-xs font-semibold text-foreground">
                    Add a Reply
                  </label>
                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    rows={3}
                    placeholder="Type your message here..."
                    className="w-full px-3 py-2 text-xs rounded-xl bg-background text-foreground border border-border focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                  />
                  <div className="flex items-center justify-between">
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => handleCloseTicket(selectedTicket.id)}
                      loading={closingTicket}
                      className="text-muted-foreground hover:text-error"
                    >
                      <Icon name="Check" size={13} className="mr-1" />
                      Close Ticket
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleReplyTicket}
                      loading={submittingReply}
                      disabled={!replyMessage.trim()}
                    >
                      <Icon name="Send" size={13} className="mr-1.5" />
                      Send Reply
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="pt-3 border-t border-border flex items-center justify-between bg-muted/20 p-3 rounded-xl">
                  <span className="text-xs text-muted-foreground">This ticket is currently marked as closed.</span>
                  <Button
                    variant="outline"
                    size="xs"
                    onClick={() => handleReopenTicket(selectedTicket.id)}
                  >
                    <Icon name="RotateCcw" size={13} className="mr-1" />
                    Reopen Ticket
                  </Button>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-border bg-card flex justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowTicketModal(false)}>
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          MODAL: ARTICLE FULL READER MODAL
         ========================================================================= */}
      {showArticleModal && selectedArticle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in-50">
          <div className="bg-card text-card-foreground rounded-2xl border border-border shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-border bg-card">
              <div className="flex items-center space-x-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded">
                  {selectedArticle.category?.name || 'General Knowledge'}
                </span>
                <span className="text-xs text-muted-foreground">• {selectedArticle.views || 0} views</span>
              </div>
              <button
                type="button"
                onClick={() => setShowArticleModal(false)}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-4 flex-1">
              <h2 className="text-2xl font-bold text-foreground">
                {selectedArticle.title}
              </h2>
              <div className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed">
                {selectedArticle.content}
              </div>

              <div className="pt-4 border-t border-border flex items-center justify-between">
                <div className="flex items-center space-x-2 text-xs">
                  <span className="text-muted-foreground">Was this helpful?</span>
                  <button
                    onClick={() => handleHelpful(selectedArticle.id, true)}
                    className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-md border border-border bg-card hover:bg-success/10 text-muted-foreground hover:text-success"
                  >
                    <Icon name="ThumbsUp" size={13} />
                    <span>Yes ({selectedArticle.helpful_count || 0})</span>
                  </button>
                </div>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => {
                    setShowArticleModal(false);
                    handleAskSupportFromFAQ(selectedArticle);
                  }}
                >
                  <Icon name="MessageSquare" size={13} className="mr-1" />
                  Ask Question About This
                </Button>
              </div>
            </div>

            <div className="p-4 border-t border-border bg-card flex justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowArticleModal(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          MODAL: PLATFORM GUIDE VIEWER MODAL
         ========================================================================= */}
      {showDocModal && activeDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in-50">
          <div className="bg-card text-card-foreground rounded-2xl border border-border shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-border bg-card">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <Icon name={activeDoc.icon} size={20} />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-[11px] font-semibold text-primary uppercase">{activeDoc.category}</span>
                    <span className="text-xs text-muted-foreground">• {activeDoc.readTime}</span>
                  </div>
                  <h3 className="text-base font-bold text-foreground">{activeDoc.title}</h3>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowDocModal(false)}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-5 flex-1">
              <p className="text-sm text-muted-foreground leading-relaxed">
                {activeDoc.summary}
              </p>

              <div className="space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Step-by-Step Instructions
                </h4>

                {activeDoc.steps.map((st) => (
                  <div key={st.step} className="p-4 rounded-xl border border-border bg-muted/20 space-y-1.5">
                    <div className="flex items-center space-x-2">
                      <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                        {st.step}
                      </span>
                      <h5 className="font-semibold text-sm text-foreground">{st.title}</h5>
                    </div>
                    <p className="text-xs text-foreground/80 pl-8 leading-relaxed">
                      {st.detail}
                    </p>
                  </div>
                ))}
              </div>

              {activeDoc.tips && (
                <div className="p-4 rounded-xl bg-primary/10 border border-primary/20 space-y-2">
                  <div className="flex items-center space-x-2 text-primary font-semibold text-xs">
                    <Icon name="Lightbulb" size={16} />
                    <span>Pro Tips & Best Practices</span>
                  </div>
                  <ul className="list-disc list-inside text-xs text-foreground/80 space-y-1 pl-1">
                    {activeDoc.tips.map((tip, idx) => (
                      <li key={idx}>{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-border bg-card flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.print()}
              >
                <Icon name="Printer" size={14} className="mr-1.5" />
                Print Guide
              </Button>
              <Button variant="default" size="sm" onClick={() => setShowDocModal(false)}>
                Done Reading
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          MODAL: REAL WORKING VIDEO TUTORIAL PLAYER MODAL
         ========================================================================= */}
      {showVideoModal && activeVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-4 animate-in fade-in-50">
          <div className="bg-card text-card-foreground rounded-2xl border border-border shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between p-3.5 sm:p-4 border-b border-border bg-card">
              <div className="flex items-center space-x-3 truncate">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                  <Icon name="PlayCircle" size={18} />
                </div>
                <div className="min-w-0 truncate">
                  <div className="flex items-center space-x-2">
                    <span className="text-[11px] font-semibold text-primary uppercase">{activeVideo.category}</span>
                    <span className="text-xs text-muted-foreground">• {activeVideo.duration}</span>
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-foreground truncate">{activeVideo.title}</h3>
                </div>
              </div>

              <div className="flex items-center space-x-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowVideoModal(false)}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto flex-1 flex flex-col">
              
              {/* THE REAL VIDEO PLAYER */}
              <div className="relative bg-black aspect-video w-full flex items-center justify-center overflow-hidden">
                {videoSourceType === 'html5' ? (
                  <video
                    key={activeVideo.id + (customVideoUrl || activeVideo.videoUrl)}
                    ref={videoRef}
                    controls
                    autoPlay
                    playsInline
                    preload="auto"
                    poster={activeVideo.thumbnail}
                    className="w-full h-full object-contain bg-black"
                  >
                    <source src={customVideoUrl || activeVideo.videoUrl} type="video/mp4" />
                    Your browser does not support HTML5 video playback.
                  </video>
                ) : (
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${activeVideo.youtubeId || 'AUZIYQGPHLRV7VgWluGU6OUCcJOGNjwmjYRaCFXJk3llrAyWYCZ6mlGFvPfQKJ978VfCgu31WCnJ5negUrpo8a6nyi2e2xbMPDIuG7UiX28CShrEMFeKWxIVc-qFxhq-ujSRSy-b'}?autoplay=1&rel=0`}
                    title={activeVideo.title}
                    className="w-full h-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )}
              </div>

              {/* Source Mode Switcher Bar */}
              <div className="bg-muted/40 border-b border-border px-4 py-2.5 flex flex-wrap items-center justify-between gap-2 text-xs">
                <div className="flex items-center space-x-2">
                  <span className="text-muted-foreground font-medium">Player Source:</span>
                  <div className="inline-flex rounded-lg border border-border bg-card p-0.5">
                    <button
                      type="button"
                      onClick={() => {
                        setVideoSourceType('html5');
                        setCustomVideoUrl('');
                      }}
                      className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                        videoSourceType === 'html5' && !customVideoUrl
                          ? 'bg-primary text-primary-foreground shadow-xs'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Icon name="Play" size={11} className="inline mr-1" />
                      HTML5 Direct Player
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setVideoSourceType('youtube');
                        setCustomVideoUrl('');
                      }}
                      className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                        videoSourceType === 'youtube'
                          ? 'bg-primary text-primary-foreground shadow-xs'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Icon name="ExternalLink" size={11} className="inline mr-1" />
                      YouTube Stream
                    </button>
                  </div>
                </div>

                {/* Custom Video URL Toggle & Speed Controls */}
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setIsCustomUrlOpen(!isCustomUrlOpen)}
                    className="text-xs text-primary hover:underline flex items-center"
                  >
                    <Icon name="Link" size={12} className="mr-1" />
                    {isCustomUrlOpen ? 'Hide Custom URL' : 'Use Custom URL'}
                  </button>

                  {/* Speed Controls for HTML5 video */}
                  {videoSourceType === 'html5' && (
                    <div className="flex items-center space-x-1.5 pl-2 border-l border-border">
                      <span className="text-muted-foreground font-medium hidden sm:inline">Speed:</span>
                      {[0.75, 1, 1.25, 1.5, 2].map((spd) => (
                        <button
                          key={spd}
                          type="button"
                          onClick={() => handleSetPlaybackSpeed(spd)}
                          className={`px-1.5 py-0.5 rounded text-[11px] font-semibold border transition-all ${
                            playbackRate === spd
                              ? 'bg-primary/10 border-primary text-primary'
                              : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted'
                          }`}
                        >
                          {spd}x
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Custom URL Input Box */}
              {isCustomUrlOpen && (
                <div className="p-3 bg-muted/20 border-b border-border flex items-center space-x-2">
                  <input
                    type="url"
                    value={tempCustomUrl}
                    onChange={(e) => setTempCustomUrl(e.target.value)}
                    placeholder="Paste direct MP4 or WebM video URL (e.g. https://.../video.mp4)"
                    className="flex-1 px-3 py-1.5 bg-background text-foreground text-xs rounded-lg border border-border focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <Button
                    variant="default"
                    size="xs"
                    onClick={() => {
                      if (tempCustomUrl.trim()) {
                        setCustomVideoUrl(tempCustomUrl.trim());
                        setVideoSourceType('html5');
                        toast.success('Custom video URL loaded');
                      }
                    }}
                  >
                    Load
                  </Button>
                </div>
              )}

              {/* Video Content & Interactive Chapters */}
              <div className="p-4 sm:p-5 space-y-5">
                
                {/* Chapters / Timeline Scrubber */}
                {activeVideo.chapters && activeVideo.chapters.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2 flex items-center">
                      <Icon name="List" size={13} className="mr-1.5 text-primary" />
                      Interactive Chapters (Click timestamp to seek video)
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                      {activeVideo.chapters.map((ch, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleSeekToChapter(ch.seconds)}
                          className="flex items-center space-x-2 p-2 rounded-lg border border-border bg-card hover:border-primary hover:bg-primary/5 transition-all text-left group"
                        >
                          <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary font-mono text-[10px] font-bold group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                            {ch.time}
                          </span>
                          <span className="text-xs text-foreground font-medium truncate">{ch.title}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Summary & Takeaways */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-border">
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                      Tutorial Summary
                    </h4>
                    <p className="text-xs text-foreground/85 leading-relaxed">
                      {activeVideo.description}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                      Key Takeaways Covered
                    </h4>
                    <ul className="space-y-1.5">
                      {activeVideo.keyTakeaways.map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-xs text-foreground/90">
                          <Icon name="CheckCircle" size={13} className="text-success mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Playlist Quick Switcher */}
                <div className="pt-3 border-t border-border">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2.5 flex items-center">
                    <Icon name="Film" size={13} className="mr-1.5 text-primary" />
                    All Tutorial Videos in This Series
                  </h4>
                  <div className="flex gap-2.5 overflow-x-auto pb-1 scrollbar-none">
                    {VIDEO_TUTORIALS.map((vid) => (
                      <button
                        key={vid.id}
                        type="button"
                        onClick={() => handleSwitchActiveVideo(vid)}
                        className={`flex-shrink-0 w-52 p-2.5 rounded-xl border text-left transition-all ${
                          activeVideo.id === vid.id
                            ? 'border-primary bg-primary/10 ring-1 ring-primary'
                            : 'border-border bg-card hover:border-primary/40'
                        }`}
                      >
                        <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
                          <span className="font-semibold text-primary">{vid.category}</span>
                          <span>{vid.duration}</span>
                        </div>
                        <h5 className="text-xs font-bold text-foreground line-clamp-1">{vid.title}</h5>
                        <div className="flex items-center space-x-1 mt-1 text-[10px] text-muted-foreground">
                          {activeVideo.id === vid.id ? (
                            <span className="text-primary font-bold flex items-center">
                              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse mr-1"></span>
                              Now Playing
                            </span>
                          ) : (
                            <span className="flex items-center hover:text-primary">
                              <Icon name="Play" size={10} className="mr-1" />
                              Click to play
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

              </div>
            </div>

            {/* Footer */}
            <div className="p-3.5 border-t border-border bg-card flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="xs"
                  onClick={handlePlayPreviousVideo}
                  disabled={VIDEO_TUTORIALS.findIndex(v => v.id === activeVideo.id) === 0}
                >
                  <Icon name="ChevronLeft" size={13} className="mr-1" />
                  Previous Video
                </Button>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={handlePlayNextVideo}
                  disabled={VIDEO_TUTORIALS.findIndex(v => v.id === activeVideo.id) === VIDEO_TUTORIALS.length - 1}
                >
                  Next Video
                  <Icon name="ChevronRight" size={13} className="ml-1" />
                </Button>
              </div>

              <Button variant="default" size="sm" onClick={() => setShowVideoModal(false)}>
                Close Player
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className={`bg-card border-t border-border py-4 transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-64 lg:ml-72'}`}>
        <div className="px-6 flex flex-col sm:flex-row items-center justify-between text-xs text-muted-foreground gap-2">
          <div className="flex items-center space-x-3">
            <span>© {new Date().getFullYear()} AssetTracker Pro</span>
            <span>•</span>
            <span>Enterprise Help & Support Desk</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="flex items-center">
              <Icon name="Mail" size={13} className="mr-1" />
              it-support@kerol.com
            </span>
            <span>•</span>
            <span className="flex items-center">
              <Icon name="Phone" size={13} className="mr-1" />
              1-800-456-7890
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HelpSupport;
